import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Edit3, UserCheck, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import PartnerBadge from "@/components/PartnerBadge";
import PaymentBadge from "@/components/PaymentBadge";
import ExportButton from "@/components/ExportButton";
import { api } from "@/lib/api";
import { formatEuro, formatDateEU, todayISO } from "@/lib/constants";

const blank = () => ({
  customer_name: "",
  contact_person: "",
  phone: "",
  email: "",
  city: "",
  address: "",
  vat_number: "",
  products_purchased: [],
  first_order_date: todayISO(),
  estimated_monthly_volume: 0,
  estimated_monthly_revenue: 0,
  actual_monthly_revenue: 0,
  purchased_batch_numbers: [],
  last_batch_delivered: "",
  favorite_sauce: "",
  sample_batch_number: "",
  sample_batches: [],
  last_contact_date: "",
  next_follow_up_date: "",
  active: true,
  notes: "",
});

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [finance, setFinance] = useState({});
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [levelFilter, setLevelFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [recomputing, setRecomputing] = useState(false);

  const load = async () => {
    const [c, m, f] = await Promise.all([
      api.listCustomers(), api.getMeta(), api.customersFinanceSummary(),
    ]);
    setCustomers(c);
    setMeta(m);
    setFinance(f);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return customers.filter((c) => {
      const levelOk = levelFilter === "all" || (c.partner_level || "Standard") === levelFilter;
      const fin = finance[c.id] || { payment_status: "none" };
      const payOk = paymentFilter === "all" || fin.payment_status === paymentFilter;
      return levelOk && payOk;
    });
  }, [customers, levelFilter, paymentFilter, finance]);

  const recomputeAll = async () => {
    setRecomputing(true);
    try {
      const r = await api.recomputeAllPartners();
      toast.success(`Recomputed ${r.updated} customer level(s)`);
      load();
    } catch { toast.error("Failed to recompute"); }
    finally { setRecomputing(false); }
  };

  const openCreate = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      ...blank(), ...c,
      products_purchased: c.products_purchased || [],
      purchased_batch_numbers: c.purchased_batch_numbers || [],
      sample_batches: c.sample_batches || [],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.customer_name) return toast.error("Customer Name is required");
    try {
      if (editing) { await api.updateCustomer(editing.id, form); toast.success("Customer updated"); }
      else { await api.createCustomer(form); toast.success("Customer created"); }
      setOpen(false);
      load();
    } catch { toast.error("Failed to save"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete customer "${c.customer_name}"?`)) return;
    await api.deleteCustomer(c.id);
    toast.success("Customer deleted");
    load();
  };

  const toggleProduct = (p) => {
    setForm((f) => ({
      ...f,
      products_purchased: f.products_purchased.includes(p)
        ? f.products_purchased.filter((x) => x !== p)
        : [...f.products_purchased, p],
    }));
  };

  if (!meta) return <div className="text-sm text-[#6C757D]">Loading…</div>;

  const totalActual = customers.filter(c => c.active).reduce((a, c) => a + (Number(c.actual_monthly_revenue) || 0), 0);
  const totalEst = customers.filter(c => c.active).reduce((a, c) => a + (Number(c.estimated_monthly_revenue) || 0), 0);

  return (
    <div data-testid="page-customers">
      <PageHeader
        overline="Recurring Revenue"
        title="Customers"
        subtitle={`${customers.filter(c=>c.active).length} active · ${formatEuro(totalActual)} actual · ${formatEuro(totalEst)} estimated /mo`}
        actions={
          <div className="flex gap-2 flex-wrap">
            <ExportButton module="customers" />
            <Button
              variant="outline"
              onClick={recomputeAll}
              disabled={recomputing}
              data-testid="recompute-partner-levels-btn"
            >
              <RefreshCw size={16} className={`mr-2 ${recomputing ? "animate-spin" : ""}`} /> Recompute Levels
            </Button>
            <Button
              onClick={openCreate}
              data-testid="add-customer-btn"
              className="bg-[#E85D04] hover:bg-[#c94b03] text-white"
            >
              <Plus size={16} className="mr-2" /> New Customer
            </Button>
          </div>
        }
      />

      <div className="card-soft p-4 mb-5 flex flex-wrap items-center gap-2" data-testid="customer-filters">
        <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold mr-2">Partner Level</Label>
        {["all", "Standard", "Partner Level 1", "Partner Level 2"].map((lvl) => (
          <button
            key={lvl}
            onClick={() => setLevelFilter(lvl)}
            data-testid={`filter-level-${lvl.replaceAll(" ", "-")}`}
            className="text-xs uppercase tracking-wider px-3 py-1.5 rounded-full font-bold transition-colors"
            style={{
              backgroundColor: levelFilter === lvl ? "#E85D04" : "#F7F1E8",
              color: levelFilter === lvl ? "white" : "#1F4D3A",
            }}
          >
            {lvl === "all" ? "All" : lvl}
          </button>
        ))}
        <span className="mx-2 h-5 w-px bg-[#E5E5E5]" />
        <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold mr-2">Payment</Label>
        {[
          ["all", "All", "#6C757D"],
          ["paid", "Paid", "#2A9D8F"],
          ["partial", "Partial", "#E76F51"],
          ["unpaid", "Outstanding", "#D62828"],
        ].map(([k, label, c]) => (
          <button
            key={k}
            onClick={() => setPaymentFilter(k)}
            data-testid={`filter-payment-${k}`}
            className="text-xs uppercase tracking-wider px-3 py-1.5 rounded-full font-bold transition-colors"
            style={{
              backgroundColor: paymentFilter === k ? c : "#F7F1E8",
              color: paymentFilter === k ? "white" : c,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {customers.length === 0 ? (
        <EmptyState
          icon={UserCheck}
          title="No customers yet"
          description="Convert a lead from the Leads page, or add a customer here."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <div key={c.id} className="card-soft card-soft-hover p-5" data-testid={`customer-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <PartnerBadge level={c.partner_level || "Standard"} testId={`customer-tier-${c.id}`} />
                    <PaymentBadge
                      status={(finance[c.id] || {}).payment_status || "none"}
                      balance={(finance[c.id] || {}).outstanding_balance || 0}
                      testId={`customer-payment-${c.id}`}
                    />
                    {!c.active && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E5E5E5] text-[#6C757D] font-bold">Inactive</span>
                    )}
                  </div>
                  <h3 className="font-display font-semibold text-lg text-[#222] truncate mt-1">{c.customer_name}</h3>
                  <div className="text-xs text-[#6C757D]">{c.city || "—"} · since {formatDateEU(c.first_order_date)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Actual /mo</div>
                  <div className="font-display font-bold text-xl text-[#2A9D8F]">{formatEuro(c.actual_monthly_revenue)}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Total Orders</div>
                  <div className="font-semibold">{formatEuro((finance[c.id] || {}).total_orders || 0)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Total Paid</div>
                  <div className="font-semibold text-[#2A9D8F]">{formatEuro((finance[c.id] || {}).total_paid || 0)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Outstanding</div>
                  <div
                    className="font-semibold"
                    style={{ color: ((finance[c.id] || {}).outstanding_balance || 0) > 0 ? "#D62828" : "#2A9D8F" }}
                  >
                    {formatEuro((finance[c.id] || {}).outstanding_balance || 0)}
                  </div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Avg / Month</div>
                  <div className="font-semibold text-[#E85D04]">{formatEuro(c.monthly_average_purchase || 0)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Last 3 Months</div>
                  <div className="font-semibold">{formatEuro(c.last_3_months_total || 0)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Volume</div>
                  <div>{c.estimated_monthly_volume || 0} L/mo</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Price Level</div>
                  <div className="text-[#1F4D3A] font-semibold">{c.current_price_level || "Standard"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Reviewed</div>
                  <div>{formatDateEU(c.review_date)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Contact</div>
                  <div className="truncate">{c.contact_person || "—"}</div>
                </div>
              </div>
              {(c.favorite_sauce || c.last_contact_date || c.next_follow_up_date || c.sample_batch_number) && (
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm border-t border-[#E5E5E5] pt-3">
                  {c.favorite_sauce && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Favorite Sauce</div>
                      <div className="font-semibold text-[#E85D04] truncate">{c.favorite_sauce}</div>
                    </div>
                  )}
                  {c.sample_batch_number && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Sample Batch</div>
                      <div className="font-mono text-xs">{c.sample_batch_number}</div>
                    </div>
                  )}
                  {c.last_contact_date && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Last Contact</div>
                      <div>{formatDateEU(c.last_contact_date)}</div>
                    </div>
                  )}
                  {c.next_follow_up_date && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Next Follow-Up</div>
                      <div className="text-[#1F4D3A] font-semibold">{formatDateEU(c.next_follow_up_date)}</div>
                    </div>
                  )}
                </div>
              )}
              {c.products_purchased?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {c.products_purchased.map((p) => (
                    <span key={p} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F7F1E8] text-[#1F4D3A] font-semibold">{p}</span>
                  ))}
                </div>
              )}
              {c.sample_batches?.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D] mb-1">Sample Batches</div>
                  <div className="flex flex-wrap gap-1.5">
                    {c.sample_batches.map((bn) => (
                      <span key={bn} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#7B6DCC]/15 text-[#7B6DCC] font-semibold border border-[#7B6DCC]/30">
                        {bn}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {c.purchased_batch_numbers?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {c.purchased_batch_numbers.map((bn) => (
                    <span key={bn} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white border border-[#E5E5E5] text-[#1F4D3A] font-semibold">
                      {bn}
                    </span>
                  ))}
                </div>
              )}
              {c.last_batch_delivered && (
                <div className="mt-1 text-[11px] text-[#6C757D]">
                  Last batch: <span className="font-mono text-[#222]">{c.last_batch_delivered}</span>
                </div>
              )}
              <div className="mt-3 flex justify-end gap-1 border-t border-[#E5E5E5] pt-3">
                <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}>
                  <Edit3 size={16} className="mr-1" /> Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={() => del(c)} className="text-[#E76F51] hover:bg-[#E76F51]/10" data-testid={`delete-customer-${c.id}`}>
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit Customer" : "New Customer"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Customer Name *">
              <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                data-testid="input-customer-name" />
            </Field>
            <Field label="Contact Person">
              <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="City">
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </Field>
            <Field label="Address (billing)" className="sm:col-span-2">
              <Textarea rows={2} value={form.address || ""}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Street, Postal code City"
                data-testid="input-customer-address" />
            </Field>
            <Field label="VAT Number">
              <Input value={form.vat_number || ""} onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                placeholder="FI12345678"
                data-testid="input-customer-vat" />
            </Field>
            <Field label="First Order Date">
              <Input type="date" value={form.first_order_date || ""} onChange={(e) => setForm({ ...form, first_order_date: e.target.value })} />
            </Field>
            <Field label="Estimated Monthly Volume (L/mo)">
              <Input type="number" min="0" value={form.estimated_monthly_volume === 0 ? "" : form.estimated_monthly_volume}
                onChange={(e) => setForm({ ...form, estimated_monthly_volume: e.target.value === "" ? "" : Number(e.target.value) })} />
            </Field>
            <Field label="Estimated Monthly Revenue (€)">
              <Input type="number" min="0" value={form.estimated_monthly_revenue === 0 ? "" : form.estimated_monthly_revenue}
                onChange={(e) => setForm({ ...form, estimated_monthly_revenue: e.target.value === "" ? "" : Number(e.target.value) })}
                data-testid="input-est-revenue" />
            </Field>
            <Field label="Actual Monthly Revenue (€)">
              <Input type="number" min="0" value={form.actual_monthly_revenue === 0 ? "" : form.actual_monthly_revenue}
                onChange={(e) => setForm({ ...form, actual_monthly_revenue: e.target.value === "" ? "" : Number(e.target.value) })}
                data-testid="input-actual-revenue" />
            </Field>
            <Field label="Active">
              <div className="flex items-center gap-3 h-10">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })}
                  data-testid="input-active" />
                <span className="text-sm">{form.active ? "Active customer" : "Inactive"}</span>
              </div>
            </Field>
            <Field label="Products Purchased" className="sm:col-span-2">
              <div className="grid grid-cols-2 gap-2">
                {meta.products.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.products_purchased.includes(p)}
                      onCheckedChange={() => toggleProduct(p)}
                    />
                    {p}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Favorite Sauce">
              <Select value={form.favorite_sauce || "__none"} onValueChange={(v) => setForm({ ...form, favorite_sauce: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="input-favorite-sauce"><SelectValue placeholder="Pick…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">—</SelectItem>
                  {meta.products.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Sample Batch Number">
              <Input
                value={form.sample_batch_number || ""}
                onChange={(e) => setForm({ ...form, sample_batch_number: e.target.value })}
                placeholder="VH-S01 (primary)"
                className="font-mono text-xs"
                data-testid="input-sample-batch-number"
              />
              <span className="text-[10px] text-[#6C757D]">Primary / featured sample batch</span>
            </Field>
            <Field label="All Sample Batches Delivered">
              <Input
                value={(form.sample_batches || []).join(", ")}
                onChange={(e) =>
                  setForm({
                    ...form,
                    sample_batches: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
                placeholder="VH-S01, MH-S01, VT-S01"
                className="font-mono text-xs"
                data-testid="input-sample-batches"
              />
              <span className="text-[10px] text-[#6C757D]">Comma-separated sample batch IDs</span>
            </Field>
            <Field label="Last Contact Date">
              <Input
                type="date"
                value={form.last_contact_date || ""}
                onChange={(e) => setForm({ ...form, last_contact_date: e.target.value })}
                data-testid="input-customer-last-contact"
              />
            </Field>
            <Field label="Next Follow-Up Date">
              <Input
                type="date"
                value={form.next_follow_up_date || ""}
                onChange={(e) => setForm({ ...form, next_follow_up_date: e.target.value })}
                data-testid="input-customer-next-followup"
              />
            </Field>
            <Field label="Purchased Batch Numbers" className="sm:col-span-2">
              <Input
                value={(form.purchased_batch_numbers || []).join(", ")}
                onChange={(e) =>
                  setForm({
                    ...form,
                    purchased_batch_numbers: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
                placeholder="VH-20260715-01, MH-20260715-01"
                className="font-mono text-xs"
                data-testid="input-purchased-batches"
              />
              <span className="text-[10px] text-[#6C757D]">Comma-separated batch IDs</span>
            </Field>
            <Field label="Last Batch Delivered" className="sm:col-span-2">
              <Input
                value={form.last_batch_delivered || ""}
                onChange={(e) => setForm({ ...form, last_batch_delivered: e.target.value })}
                placeholder="VH-20260715-01"
                className="font-mono text-xs"
                data-testid="input-last-batch"
              />
            </Field>
            <Field label="Notes" className="sm:col-span-2">
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-customer">Cancel</Button>
            <Button onClick={save} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="save-customer">
              {editing ? "Save Changes" : "Create Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">{label}</Label>
    {children}
  </div>
);
